import type { CollectionReference, Firestore } from 'firebase/firestore'
import {
  collection,
  doc,
  getAggregateFromServer,
  query,
  where,
  count,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  serverTimestamp,
} from 'firebase/firestore'
import {
  type FirestoreOrganizationSectionPassCouponFolder,
  schema,
} from './schema'
import { collectionSnapshots } from '../../firestore-mobx/stream'
import { updateDocWithError } from '../../firestore-mobx/fetch'

const converter: FirestoreDataConverter<FirestoreOrganizationSectionPassCouponFolder> =
  {
    toFirestore: (data) => {
      return data
    },
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return schema.parse(data)
    },
  }

export const getOrganizationSectionPassCouponFolderColRef = (
  firestore: Firestore,
  organizationId: string
): CollectionReference<FirestoreOrganizationSectionPassCouponFolder> => {
  return collection(
    firestore,
    'organization',
    organizationId,
    'section_pass_coupon_folder'
  ).withConverter(converter)
}

export const getOrganizationSectionPassCouponFolderDocRef = (
  firestore: Firestore,
  organizationId: string,
  folderId: string
) => {
  return doc(
    getOrganizationSectionPassCouponFolderColRef(firestore, organizationId),
    folderId
  )
}

export const getOrganizationCouponFoldersWithAvailableAndExpiredCounts = (
  firestore: Firestore,
  organizationId: string
) => {
  //parse the ids via a stream, not a catalog just care about the ids
  const couponFoldersRef = collection(
    firestore,
    'organization',
    organizationId,
    'section_pass_coupon_folder'
  ).withConverter(converter)

  const nonExpiredQuery = query(
    couponFoldersRef,
    where('expiresAt', '>=', new Date())
  )

  const snapshots = collectionSnapshots(nonExpiredQuery)

  return snapshots.asyncMap(async (snapshot) => {
    // map docs to folders with counts initialized to 0, filtering out hidden folders
    const folders = snapshot.docs
      .map((doc) => {
        const data = doc.data()
        return {
          ...data,
          id: doc.id,
          ref: doc.ref,
          activeCount: 0,
          consumedCount: 0,
        }
      })
      .filter((folder) => folder.show !== false) // Only show folders that are not hidden

    for (const folder of folders) {
      const couponsColRef = collection(folder.ref, 'section_pass_coupon')
      const activeCountQuery = getAggregateFromServer(
        query(couponsColRef, where('consumed', '==', false)),
        { count: count() }
      )
      const consumedCountQuery = getAggregateFromServer(
        query(couponsColRef, where('consumed', '==', true)),
        { count: count() }
      )
      await Promise.all([activeCountQuery, consumedCountQuery]).then(
        ([activeSnap, consumedSnap]) => {
          const activeCount = activeSnap.data().count
          const consumedCount = consumedSnap.data().count
          folder.activeCount = activeCount
          folder.consumedCount = consumedCount
        }
      )
    }

    return folders.map((folder) => ({
      id: folder.id,
      name: folder.folderName,
      updatedAt: folder.updatedAt,
      organizationId: folder.organizationId,
      activeCount: folder.activeCount,
      consumedCount: folder.consumedCount,
      expiresAt: folder.expiresAt,
    }))
  })
}

/**
 * Updates folder name and/or expiration date
 */
export async function updateFolderDetails({
  firestore,
  organizationId,
  folderId,
  folderName,
  expiresAt,
}: {
  firestore: Firestore
  organizationId: string
  folderId: string
  folderName?: string
  expiresAt?: Date | null
}) {
  const folderDocRef = doc(
    firestore,
    'organization',
    organizationId,
    'section_pass_coupon_folder',
    folderId
  )

  const updateData: {
    folderName?: string
    expiresAt?: Date | null
    updatedAt: ReturnType<typeof serverTimestamp>
  } = {
    updatedAt: serverTimestamp(),
  }

  if (folderName !== undefined) {
    updateData.folderName = folderName
  }

  if (expiresAt !== undefined) {
    updateData.expiresAt = expiresAt
  }

  await updateDocWithError(folderDocRef, updateData, 'UpdateFolderDetailsError')
}

/**
 * Soft deletes a folder by setting expiration to now and show to false
 */
export async function deleteFolderWithCoupons({
  firestore,
  organizationId,
  folderId,
}: {
  firestore: Firestore
  organizationId: string
  folderId: string
}) {
  const folderDocRef = doc(
    firestore,
    'organization',
    organizationId,
    'section_pass_coupon_folder',
    folderId
  )

  // Soft delete by setting expiration to now and hiding from UI
  await updateDocWithError(
    folderDocRef,
    {
      expiresAt: serverTimestamp(),
      show: false,
      updatedAt: serverTimestamp(),
    },
    'UpdateFolderForSoftDeleteError'
  )
}
